/**
 * AnalyticsWrapper wraps something that mounts StreamItems. Its children should be a function that take
 * registerStreamItemNode and unregisterStreamItemNode as render props. It implements StreamItemTracker 
 * to track DOM Nodes that are in view and DOM nodes that are candidates.
 */

import React, { Component } from "react";
import PropTypes from "prop-types";
import { Map } from "immutable";

import StreamItemsTracker from "./utils/StreamItemsTracker";

import { trackCardViewedInStream } from "utils/analytics";
import { STREAM_TYPE, STREAM_TRACKING } from "config/constants.js";

class AnalyticsWrapper extends Component {

  constructor(props) {
    super(props);

    this.state = { isDisabled: props.disableAnalytics && props.disableUrlUpdating };

    this.untrackedItems = []; // Store items that mounted before AnalyticsWrapper can mount (for SSR)
    this.registerStreamItemNode = this._registerStreamItemNode.bind(this);
    this.unregisterStreamItemNode = this._unregisterStreamItemNode.bind(this);
    this.onItemViewedInStream = this._onItemViewedInStream.bind(this);
    this.updateBrowserHistory = this._updateBrowserHistory.bind(this);
    this.rafHandleScroll = () => requestAnimationFrame(this._handleScroll.bind(this)); // debounce using rAF
  }

  componentDidMount() {
    if (this.state.isDisabled) { return }

    this.tracker = new StreamItemsTracker(
      STREAM_TRACKING.TIMEOUT_LENGTH,
      (this.props.disableAnalytics ? () => {} : this.onItemViewedInStream),
      (this.props.disableUrlUpdating ? () => {} : this.updateBrowserHistory),
    );

    // add items that mounted before AnalyticsWrapper could mount to the tracker (for server-side rendering)
    this.untrackedItems.forEach(({ domNode, edge, position }) => {
      this.tracker.track(domNode, edge, position);
    });

    window.addEventListener("scroll", this.rafHandleScroll);
    this.tracker.trackFirstItem();
  }

  componentWillUnmount() {
    if (this.state.isDisabled) { return }

    window.removeEventListener("scroll", this.rafHandleScroll);
    this.tracker.disconnect();
  }

  /**
   * A DOM Node getting candidacy starts a timeout that, when elapsed, will call onItemViewedInStream.
   * There are two scenarios where a story can be given candidacy.
   * 1. The entirety of the story is in the viewport
   * 2. A story occupies a percentage of the vertical height of the viewport
   * If neither of those is true, a stream item is stripped of its candidacy and the timeout before calling 
   * onItemViewedInStream is cleared. 
   * 
   * handleScroll loops over tracker.getInViewTrackedItems instead of tracker.trackedItemMap to reduce the number
   * of DOM calculations.
   */
  _handleScroll() {
    this.tracker.getInViewTrackedItems().forEach((item, domNode) => {
      const rect = domNode.getBoundingClientRect();

      const topOffscreen = rect.top < 0 ? Math.abs(rect.top) : 0;
      const bottomOffscreen = rect.bottom > window.innerHeight ? rect.bottom - window.innerHeight : 0;
      const amountOnscreen = rect.height - (topOffscreen + bottomOffscreen);

      if (amountOnscreen / rect.height >= STREAM_TRACKING.ELEMENT_RATIO) {
        this.tracker.handleDomNodeCandidacy(domNode);
      } else if (amountOnscreen / window.innerHeight >= STREAM_TRACKING.VIEWPORT_RATIO) {
        this.tracker.handleDomNodeCandidacy(domNode);
      } else {
        this.tracker.handleRemoveDomNodeCandidacy(domNode);
      }
    });
  }

  /**
   * Set the url to the permalink of candidate with the highest position.
   */
  _updateBrowserHistory() {
    const highestCandidate = this.tracker.getCandidates().reduce((a, b) => {
      return a.get("position") < b.get("position") ? a : b;
    }, Map());
    if (highestCandidate.get("edge")) {
      history.replaceState({}, "", highestCandidate.get("edge").node.permalink);
    }
  }

  _onItemViewedInStream({ edge, position }) {
    trackCardViewedInStream({
      story: edge.node,
      stream: {
        lookup: this.props.stream.lookup,
        type: this.props.stream.type,
        displayName: this.props.stream.displayName,
      },
      featured: edge.isFeatured,
      position: position,
    });
  }

  /**
   * Children mount before their parents, which means there is no way to guarentee that this.tracker is defined
   * before StreamItems call registerStreamItemNode. If this.tracker is undefined, store the item to track in 
   * this.untrackedItems. Those untracked items will be added to the tracker when AnalyticsWrapper is mounted.
   */
  _registerStreamItemNode(domNode, edge, position) {
    if (!this.tracker) {
      this.untrackedItems.push({ domNode, edge, position });
    } else {
      this.tracker.track(domNode, edge, position);
    }
  }

  _unregisterStreamItemNode(domNode) {
    this.tracker.untrack(domNode);
  }

  render() {
    return (
      <React.Fragment>
        { this.props.children({
          registerStreamItemNode: this.state.isDisabled ? () => {} : this.registerStreamItemNode,
          unregisterStreamItemNode: this.state.isDisabled ? () => {} : this.unregisterStreamItemNode,
        })}
      </React.Fragment>
    );
  }
}

AnalyticsWrapper.propTypes = {
  children: PropTypes.func.isRequired,
  stream: PropTypes.shape({
    type: PropTypes.oneOf(Object.values(STREAM_TYPE)).isRequired,
    lookup: PropTypes.string.isRequired,
    displayName: PropTypes.string,
  }).isRequired,
  disableAnalytics: PropTypes.bool,
  disableUrlUpdating: PropTypes.bool,
};

export default AnalyticsWrapper;
